import "./MenuSearch.scss";

import React, { useMemo, useState } from "react";

import { AutoComplete, Input } from "antd";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import {
  BulbOutlined,
  DollarCircleOutlined,
  HomeOutlined,
  MessageOutlined,
  SearchOutlined,
  SettingOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { MENU_STRUCTURE } from "../../routeConstants";

const MenuSearch = ({ isRestricted, allowedFunctions, onNavigate }) => {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();

  // Icon mapping
  const iconMap = {
    UserOutlined: <UserOutlined />,
    TeamOutlined: <TeamOutlined />,
    ShoppingCartOutlined: <ShoppingCartOutlined />,
    DollarCircleOutlined: <DollarCircleOutlined />,
    ShareAltOutlined: <ShareAltOutlined />,
    BulbOutlined: <BulbOutlined />,
    MessageOutlined: <MessageOutlined />,
    SettingOutlined: <SettingOutlined />,
    ToolOutlined: <ToolOutlined />,
    HomeOutlined: <HomeOutlined />,
  };

  // Filter function to check if user has access to a menu item
  const hasAccess = (itemKey) => {
    return isRestricted ? allowedFunctions?.includes(itemKey) : true;
  };

  // Get all accessible menu items with their parent context
  const getAllAccessibleMenuItems = useMemo(() => {
    const allItems = [];

    MENU_STRUCTURE.forEach((section) => {
      if (section.type === "group") {
        section.items.forEach((item) => {
          if (hasAccess(item.key)) {
            allItems.push({
              ...item,
              parentLabel: section.label,
              parentIcon: iconMap.HomeOutlined,
              sectionType: "group",
            });
          }
        });
      } else if (section.type === "submenu") {
        section.items.forEach((item) => {
          if (hasAccess(item.key)) {
            allItems.push({
              ...item,
              parentLabel: section.label,
              parentIcon: iconMap[section.icon] || iconMap.SettingOutlined,
              sectionType: "submenu",
              sectionKey: section.key,
            });
          }
        });
      }
    });

    return allItems;
  }, [isRestricted, allowedFunctions]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) return [];

    const query = searchValue.toLowerCase();
    return getAllAccessibleMenuItems
      .filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(query);
        const parentMatch = item.parentLabel.toLowerCase().includes(query);
        return nameMatch || parentMatch;
      })
      .slice(0, 8); // Limit to 8 results for better UX
  }, [searchValue, getAllAccessibleMenuItems]);

  // Handle search input change
  const handleSearch = (value) => {
    setSearchValue(value);
    setIsOpen(value.trim().length > 0);
  };

  // Handle clear button click
  const handleClear = () => {
    setSearchValue("");
    setIsOpen(false);
  };

  // Handle item selection
  const handleSelect = (value) => {
    const selectedItem = filteredItems.find((item) => item.key === value);
    if (selectedItem) {
      setSearchValue("");
      setIsOpen(false);

      // Call navigation callback if provided
      if (onNavigate) {
        onNavigate(selectedItem);
      }

      // Navigate to the route
      history.push(selectedItem.route);
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (searchValue.trim().length > 0) {
      setIsOpen(true);
    }
  };

  // Handle blur
  const handleBlur = () => {
    // Delay closing to allow for option selection
    setTimeout(() => setIsOpen(false), 200);
  };

  // Format search results for AutoComplete
  const searchOptions = filteredItems.map((item) => ({
    value: item.key,
    label: (
      <div className="menu-search-option">
        <div className="option-icon">{item.parentIcon}</div>
        <div className="option-content">
          <div className="option-name">{item.name}</div>
          <div className="option-parent">{item.parentLabel}</div>
        </div>
      </div>
    ),
  }));

  return (
    <div className="menu-search-container">
      <AutoComplete
        value={searchValue}
        options={searchOptions}
        onSelect={handleSelect}
        onSearch={handleSearch}
        onClear={handleClear}
        open={isOpen}
        onFocus={handleFocus}
        onBlur={handleBlur}
        allowClear
        style={{ width: "100%" }}
        dropdownMatchSelectWidth={false}
        dropdownClassName="menu-search-dropdown"
        notFoundContent={
          searchValue.trim() ? (
            <div className="no-results">No matching pages found</div>
          ) : null
        }
      >
        <Input
          size="large"
          prefix={<SearchOutlined />}
          placeholder="Search..."
          className="menu-search-input"
        />
      </AutoComplete>
    </div>
  );
};

MenuSearch.propTypes = {
  isRestricted: PropTypes.bool.isRequired,
  allowedFunctions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onNavigate: PropTypes.func,
};

export default MenuSearch;
